import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Alert, Table, TableBody,
  TableRow, TableCell, Divider, Grid, CircularProgress, Chip
} from '@mui/material';
import { ArrowBack, Print, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym, locale } from '../utils/currency';
import CompanyHeader from './CompanyHeader';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtD = (d) => d ? new Date(d).toLocaleDateString(locale(), { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

const PayslipView = () => {
  const navigate = useNavigate();
  const { runNo, employeeNo } = useParams();

  const [entry,   setEntry]   = useState(null);
  const [run,     setRun]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [eRes, rRes] = await Promise.all([
          axios.get(`${API}/api/payroll/runs/${runNo}/entries/${employeeNo}`, { headers: hdrs() }),
          axios.get(`${API}/api/payroll/runs/${runNo}`, { headers: hdrs() }),
        ]);
        setEntry(eRes.data);
        setRun(rRes.data);
      } catch { setError('Failed to load payslip'); }
      finally { setLoading(false); }
    };
    load();
  }, [runNo, employeeNo]);

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>
  );
  if (error)  return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  if (!entry) return <Alert severity="warning" sx={{ m: 3 }}>Entry not found</Alert>;

  const earnings       = entry.earnings       || [];
  const deductions     = entry.deductions     || [];
  const nonCashBenefits = entry.nonCashBenefits || [];
  const totalEarnings   = earnings.reduce((s, e) => s + e.amount, 0);
  const totalDeductions = deductions.reduce((s, d) => s + d.amount, 0);
  const totalNonCash    = nonCashBenefits.reduce((s, b) => s + b.amount, 0);

  return (
    <Box sx={{ maxWidth: 750, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .payslip-card { box-shadow: none !important; border: 1px solid #ccc !important; }
        }
      `}</style>

      {/* Controls — hidden on print */}
      <Box display="flex" gap={1.5} mb={2} className="no-print">
        <Button startIcon={<ArrowBack />} variant="outlined" size="small"
          onClick={() => navigate(`/payroll/runs/${runNo}`)}>
          Back to Run
        </Button>
        <Button startIcon={<Print />} variant="contained" size="small"
          onClick={() => window.print()}
          sx={{ bgcolor: '#263238', '&:hover': { bgcolor: '#37474f' } }}>
          Print / Save PDF
        </Button>
      </Box>

      {/* ── Payslip ── */}
      <Card elevation={3} className="payslip-card">
        <CardContent sx={{ p: 3 }}>

          {/* Header */}
          <CompanyHeader title="Payslip" subtitle={`${run?.periodName || ''} · ${runNo}`} />

          {/* A-Third Rule violation warning */}
          {entry.aThirdRuleViolated && (
            <Alert
              severity="warning"
              icon={<WarningAmber fontSize="small" />}
              sx={{ mb: 2, py: 0.5 }}
            >
              <Typography variant="caption" fontWeight={700}>
                A-Third Rule Violation (Kenya Employment Act s.19)
              </Typography>
              <Typography variant="caption" display="block">
                Net pay of {sym()} {fmt(entry.netPay)} is below 1/3 of basic salary. Review voluntary deductions with the employee.
              </Typography>
            </Alert>
          )}

          {/* Employee details */}
          <Box mb={2} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
            {[
              [{ label: 'Employee Name', value: <Typography variant="body2" fontWeight={700}>{entry.employeeName}</Typography> },
               { label: 'Employee No',   value: <Typography variant="body2" fontFamily="monospace">{entry.employeeNo}</Typography> }],
              [{ label: 'Department',    value: entry.department || '—' },
               { label: 'Job Title',     value: entry.jobTitle   || '—' }],
              [{ label: 'Bank / Account', value: `${entry.bankName || '—'}${entry.bankAccount ? ' / ' + entry.bankAccount : ''}` },
               { label: 'Pay Period',    value: run?.periodName  || '—' }],
            ].map((row, ri) => (
              <Box key={ri} display="flex" sx={{ borderBottom: ri < 2 ? '1px solid #e0e0e0' : 0, bgcolor: ri % 2 === 0 ? '#fafafa' : '#fff' }}>
                {row.map((cell, ci) => (
                  <Box key={ci} flex={1} px={2} py={1.2} sx={{ borderRight: ci === 0 ? '1px solid #e0e0e0' : 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.4}>
                      {cell.label}
                    </Typography>
                    {typeof cell.value === 'string'
                      ? <Typography variant="body2" mt={0.3}>{cell.value}</Typography>
                      : <Box mt={0.3}>{cell.value}</Box>}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>

          <Grid container spacing={2}>
            {/* Earnings */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" fontWeight={700} mb={1} color="#1565c0">EARNINGS</Typography>
              <Table size="small">
                <TableBody>
                  {earnings.map((e, i) => (
                    <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ py: 0.5, pl: 0 }}>
                        <Typography variant="body2">{e.description}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, pr: 0 }}>
                        <Typography variant="body2">{fmt(e.amount)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ py: 0.8, pl: 0, borderTop: '1px solid #e0e0e0', borderBottom: 0 }}>
                      <Typography variant="body2" fontWeight={700}>Total Earnings</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.8, pr: 0, borderTop: '1px solid #e0e0e0', borderBottom: 0 }}>
                      <Typography variant="body2" fontWeight={700}>{fmt(totalEarnings)}</Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>

            {/* Deductions */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" fontWeight={700} mb={1} color="#c62828">DEDUCTIONS</Typography>
              <Table size="small">
                <TableBody>
                  {deductions.map((d, i) => (
                    <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ py: 0.5, pl: 0 }}>
                        <Typography variant="body2">{d.description}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, pr: 0 }}>
                        <Typography variant="body2" color="error.main">{fmt(d.amount)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ py: 0.8, pl: 0, borderTop: '1px solid #e0e0e0', borderBottom: 0 }}>
                      <Typography variant="body2" fontWeight={700}>Total Deductions</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.8, pr: 0, borderTop: '1px solid #e0e0e0', borderBottom: 0 }}>
                      <Typography variant="body2" fontWeight={700} color="error.main">{fmt(totalDeductions)}</Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
          </Grid>

          {/* Benefits in Kind (Non-Cash Benefits) */}
          {nonCashBenefits.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="subtitle2" fontWeight={700} color="#6a1b9a">
                    BENEFITS IN KIND
                  </Typography>
                  <Chip
                    label="Taxable — not paid in cash"
                    size="small"
                    sx={{ fontSize: 10, height: 18, bgcolor: '#f3e5f5', color: '#6a1b9a' }}
                  />
                </Box>
                <Table size="small">
                  <TableBody>
                    {nonCashBenefits.map((b, i) => (
                      <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell sx={{ py: 0.5, pl: 0 }}>
                          <Typography variant="body2">{b.description}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, pr: 0 }}>
                          <Typography variant="body2" color="#6a1b9a">{fmt(b.amount)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ py: 0.8, pl: 0, borderTop: '1px solid #e0e0e0', borderBottom: 0 }}>
                        <Typography variant="body2" fontWeight={700}>Total Benefits in Kind</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Included in taxable pay ({sym()} {fmt(entry.taxablePay)}) — no cash impact on net pay
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.8, pr: 0, borderTop: '1px solid #e0e0e0', borderBottom: 0, verticalAlign: 'top' }}>
                        <Typography variant="body2" fontWeight={700} color="#6a1b9a">{fmt(totalNonCash)}</Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </>
          )}

          {/* Taxable pay note */}
          <Box mt={1.5} sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Taxable Pay: {sym()} {fmt(entry.taxablePay)} &nbsp;|&nbsp;
              Gross Pay (cash): {sym()} {fmt(entry.grossPay)}
              {(entry.excessPensionTax || 0) > 0 && (
                <> &nbsp;|&nbsp; Excess Pension Tax: {sym()} {fmt(entry.excessPensionTax)}</>
              )}
            </Typography>
          </Box>

          {/* PAYE Relief breakdown — shown when employee pays PAYE */}
          {(entry.paye > 0 || entry.personalRelief > 0) && (
            <Box mt={1} sx={{ border: '1px solid #e3f2fd', borderRadius: 1, p: 1.5, bgcolor: '#f8fbff' }}>
              <Typography variant="caption" fontWeight={700} color="primary.main" textTransform="uppercase"
                letterSpacing={0.8} display="block" mb={0.5}>
                PAYE Computation
              </Typography>
              <Table size="small" sx={{ '& td': { py: 0.3, px: 0.5, border: 0 } }}>
                <TableBody>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="text.secondary">Chargeable Tax on {sym()} {fmt(entry.taxablePay)}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="caption">{sym()} {fmt(entry.paye + (entry.personalRelief || 0) + (entry.insuranceRelief || 0))}</Typography></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="caption" color="success.main">Less: Personal Relief</Typography></TableCell>
                    <TableCell align="right"><Typography variant="caption" color="success.main">({sym()} {fmt(entry.personalRelief || 0)})</Typography></TableCell>
                  </TableRow>
                  {(entry.insuranceRelief || 0) > 0 && (
                    <TableRow>
                      <TableCell><Typography variant="caption" color="success.main">Less: Insurance Relief (15% of premium)</Typography></TableCell>
                      <TableCell align="right"><Typography variant="caption" color="success.main">({sym()} {fmt(entry.insuranceRelief)})</Typography></TableCell>
                    </TableRow>
                  )}
                  <TableRow sx={{ borderTop: '1px solid #90caf9' }}>
                    <TableCell sx={{ pt: '4px !important' }}><Typography variant="caption" fontWeight={700} color="primary.main">PAYE Payable</Typography></TableCell>
                    <TableCell align="right" sx={{ pt: '4px !important' }}><Typography variant="caption" fontWeight={700} color="primary.main">{sym()} {fmt(entry.paye)}</Typography></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Employer contributions */}
          <Box mt={1} sx={{ border: '1px dashed #ccc', p: 1.5, borderRadius: 1 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" display="block" mb={0.5}>
              Employer Contributions (not deducted from employee pay)
            </Typography>
            <Box display="flex" gap={4}>
              <Typography variant="caption">Housing Levy ER: {sym()} {fmt(entry.housingLevyER)}</Typography>
              <Typography variant="caption">NSSF ER: {sym()} {fmt(entry.nssfER)}</Typography>
            </Box>
          </Box>

          {/* Net Pay — always last */}
          <Divider sx={{ my: 2 }} />
          <Box sx={{ bgcolor: '#263238', color: '#fff', p: 2, borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={700}>NET PAY</Typography>
            <Typography variant="h5" fontWeight={800}>{sym()} {fmt(entry.netPay)}</Typography>
          </Box>

          {/* Footer */}
          <Box mt={2} textAlign="center">
            <Typography variant="caption" color="text.disabled">
              Generated: {fmtD(new Date())} · This is a computer-generated payslip.
            </Typography>
          </Box>

        </CardContent>
      </Card>
    </Box>
  );
};

export default PayslipView;
